import { createContext, useContext, useEffect, useCallback, useMemo } from "react";
import { FormValidation } from "./types";

type UseFormType<T> = {
  onSubmit: () => void;
} & FormValidation<T>;

const StateContext = createContext({});

export const useForm = <T,>(): UseFormType<T> => useContext(StateContext) as UseFormType<T>;

type FormProps<T> = {
  disabled?: boolean;
  onSubmit?: (event?: React.FormEvent<HTMLFormElement>) => void;
  onError?: (event?: React.FormEvent<HTMLFormElement>) => void;
  children?: ((props: { onSubmit: () => void }) => React.ReactNode) | React.ReactNode;
} & FormValidation<T>;

const Form = <T extends Record<string, any>>({
  fields,
  validateFields,
  setFieldErrors,
  resetFields,
  isValid,
  wasValidOnMount,
  reset,
  onSubmit,
  values,
  saveChanges,
  hasFields,
  dispatch,
  onError,
  disabled,
  unsavedChanges,
  ...rest
}: FormProps<T>): React.ReactElement => {
  const saveOnClick = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      const hasErrors = validateFields();
      if (hasErrors) {
        if (onError) {
          onError(event);
        }
      } else {
        await new Promise((resolve) => {
          saveChanges();
          setTimeout(resolve, 0);
        });
        if (onSubmit) onSubmit(event);
      }
    },
    [saveChanges, onSubmit, onError, validateFields]
  );

  useEffect(() => {
    const saveFunc = (event: KeyboardEvent) => {
      // CTRL/CMD + S
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
          saveOnClick();
        }
        return false;
      }
      return true;
    };

    window.addEventListener("keydown", saveFunc);

    return () => {
      window.removeEventListener("keydown", saveFunc);
    };
  }, [disabled, saveOnClick]);

  const children = typeof rest.children === "function" ? rest.children({ onSubmit: saveOnClick }) : rest.children;

  const context = useMemo(
    () =>
      ({
        isValid,
        fields,
        validateFields,
        setFieldErrors,
        resetFields,
        reset,
        hasFields,
        values,
        saveChanges,
        unsavedChanges,
        wasValidOnMount,
        dispatch,
        onSubmit: saveOnClick,
      } as UseFormType<T>),
    [
      fields,
      isValid,
      saveChanges,
      resetFields,
      setFieldErrors,
      reset,
      saveOnClick,
      unsavedChanges,
      wasValidOnMount,
      validateFields,
      values,
      dispatch,
      hasFields,
    ]
  );

  return (
    <StateContext.Provider value={context}>
      <form
        onReset={(e) => {
          e.preventDefault();
          reset();
        }}
        onSubmit={(e) => {
          e.preventDefault();
          saveOnClick(e);
        }}
      >
        {children}
      </form>
    </StateContext.Provider>
  );
};

export default Form;
